import {
  Component,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import {
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AppSvgIconComponent, OverlayService } from '@vallift/ngl';
import {
  AnswerDetailsPreviewOverlayComponent,
  type AnswerDetailsPreviewOverlayData,
} from '../../components/answer-details-preview-overlay/answer-details-preview-overlay';
import {
  EntityRecordPreviewOverlayComponent,
  type EntityRecordPreviewOverlayData,
} from '../../components/entity-record-preview-overlay/entity-record-preview-overlay';
import {
  LocationDetailsPreviewOverlayComponent,
  type LocationDetailsPreviewOverlayData,
} from '../../components/location-details-preview-overlay/location-details-preview-overlay';
import type { FormSubmissionListItem } from '../../models/list-form-submissions';
import { AnswerPreviewHostService } from '../../services/answer-preview-host.service';
import {
  buildAnswerDetailSections,
  formatAnswerValue,
  isAddressLikeAnswer,
  isAnswerHoverable,
  isCollectionEntityAnswer,
  readAddressLikeAnswer,
  readEntityAnswerId,
} from '../../utils/form-submission-answer-display';

const OPEN_DELAY_MS = 280;
const CLOSE_DELAY_MS = 200;

type PreviewKind = 'entity' | 'details' | 'location';

@Component({
  selector: 'app-form-submission-answer-cell',
  standalone: true,
  imports: [AppSvgIconComponent],
  host: {
    class: 'block w-full min-w-0',
  },
  template: `
    @if (isEntity) {
      <div
        #previewTrigger
        class="-mx-1 -my-1 flex w-full min-w-0 cursor-default items-center gap-1.5 px-1 py-1 text-left text-sky-700"
        (mouseenter)="onPreviewEnter()"
        (mouseleave)="onPreviewLeave()">
        <app-svg-icon
          class="shrink-0 text-sky-600"
          src="assets/icons/database-outline.svg"
          [size]="14" />
        <span
          class="min-w-0 flex-1 whitespace-nowrap underline decoration-dotted decoration-sky-400 underline-offset-2">
          {{ label }}
        </span>
      </div>
    } @else if (isLocation) {
      <button
        #previewTrigger
        type="button"
        class="-mx-1 -my-1 flex w-full min-w-0 cursor-pointer items-center gap-1.5 px-1 py-1 text-left text-sky-700"
        (mouseenter)="onPreviewEnter()"
        (mouseleave)="onPreviewLeave()"
        (click)="onLocationClick($event)">
        <app-svg-icon
          class="shrink-0 text-sky-600"
          src="assets/icons/components/geo-location.svg"
          [size]="14" />
        <span
          class="min-w-0 flex-1 whitespace-nowrap underline decoration-dotted decoration-sky-400 underline-offset-2">
          {{ label }}
        </span>
      </button>
    } @else if (isHoverable) {
      <span
        #previewTrigger
        class="block w-full cursor-help whitespace-nowrap underline decoration-dotted decoration-neutral-400 underline-offset-2"
        (mouseenter)="onPreviewEnter()"
        (mouseleave)="onPreviewLeave()">
        {{ label }}
      </span>
    } @else {
      <span class="block w-full whitespace-nowrap">{{ label }}</span>
    }
  `,
})
export class FormSubmissionAnswerCellComponent implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly overlayService = inject(OverlayService);
  private readonly injector = inject(Injector);
  private readonly previewHost = inject(AnswerPreviewHostService);
  private readonly previewTrigger =
    viewChild<ElementRef<HTMLElement>>('previewTrigger');

  /** Set by NGL DynamicRenderer. */
  rowData!: FormSubmissionListItem;
  /** Set by NGL DynamicRenderer from column.customConfig.data. */
  data!: { fieldId: string };

  private overlayRef: OverlayRef | null = null;
  private openingKey: string | null = null;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private pointerInsideCard = false;
  private paneEnterHandler: (() => void) | null = null;
  private paneLeaveHandler: (() => void) | null = null;

  get value(): unknown {
    const fieldId = this.data?.fieldId;
    if (!fieldId) {
      return undefined;
    }
    return this.rowData?.answers?.[fieldId];
  }

  get label(): string {
    return formatAnswerValue(this.value);
  }

  get isEntity(): boolean {
    return isCollectionEntityAnswer(this.value);
  }

  get isLocation(): boolean {
    return isAddressLikeAnswer(this.value);
  }

  get isHoverable(): boolean {
    return !this.isEntity && !this.isLocation && isAnswerHoverable(this.value);
  }

  ngOnDestroy(): void {
    this.clearOpenTimer();
    this.clearCloseTimer();
    this.closePreview();
  }

  onPreviewEnter(): void {
    if (this.isLocation && this.isCoarsePointer()) {
      return;
    }
    this.clearCloseTimer();
    this.scheduleOpen();
  }

  onPreviewLeave(): void {
    this.clearOpenTimer();
    this.scheduleClose();
  }

  onLocationClick(event: MouseEvent): void {
    if (!this.isLocation) {
      return;
    }
    if (!this.isCoarsePointer()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.openLocationSheet();
  }

  private previewKind(): PreviewKind | null {
    if (this.isEntity) {
      return 'entity';
    }
    if (this.isLocation) {
      return 'location';
    }
    if (this.isHoverable) {
      return 'details';
    }
    return null;
  }

  private previewKey(kind: PreviewKind): string {
    if (kind === 'entity') {
      return `entity:${readEntityAnswerId(this.value) ?? ''}`;
    }
    if (kind === 'location') {
      const address = readAddressLikeAnswer(this.value);
      return `location:${this.data?.fieldId ?? ''}:${address?.placeId ?? this.label}`;
    }
    return `details:${this.data?.fieldId ?? ''}:${this.label}`;
  }

  private scheduleOpen(): void {
    const kind = this.previewKind();
    if (!kind) {
      return;
    }

    const key = this.previewKey(kind);
    if (this.overlayRef?.hasAttached() && this.openingKey === key) {
      return;
    }

    this.clearOpenTimer();
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.openPreview();
    }, OPEN_DELAY_MS);
  }

  private openPreview(): void {
    const kind = this.previewKind();
    if (!kind) {
      return;
    }

    const key = this.previewKey(kind);
    if (this.overlayRef?.hasAttached() && this.openingKey === key) {
      return;
    }

    const trigger = this.previewTrigger()?.nativeElement;
    if (!trigger) {
      return;
    }

    this.previewHost.claim(this, () => this.closePreview());
    this.closePreviewLocal();
    this.openingKey = key;

    const pointerCallbacks = {
      onPointerEnter: () => {
        this.pointerInsideCard = true;
        this.clearCloseTimer();
      },
      onPointerLeave: () => {
        this.pointerInsideCard = false;
        this.scheduleClose();
      },
    };

    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(trigger)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: -2,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: 2,
        },
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
          offsetY: -2,
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
    });

    if (kind === 'entity') {
      const itemId = readEntityAnswerId(this.value);
      if (!itemId) {
        this.closePreviewLocal();
        this.previewHost.release(this);
        return;
      }

      const data: EntityRecordPreviewOverlayData = {
        itemId,
        display: this.label,
        ...pointerCallbacks,
      };

      const portalInjector = Injector.create({
        parent: this.injector,
        providers: [{ provide: DIALOG_DATA, useValue: data }],
      });

      this.overlayRef.attach(
        new ComponentPortal(
          EntityRecordPreviewOverlayComponent,
          null,
          portalInjector,
        ),
      );
    } else if (kind === 'location') {
      const address = readAddressLikeAnswer(this.value);
      if (!address) {
        this.closePreviewLocal();
        this.previewHost.release(this);
        return;
      }

      const data: LocationDetailsPreviewOverlayData = {
        ...address,
        sheetMode: false,
        onClose: () => this.closePreview(),
        ...pointerCallbacks,
      };

      const portalInjector = Injector.create({
        parent: this.injector,
        providers: [{ provide: DIALOG_DATA, useValue: data }],
      });

      this.overlayRef.attach(
        new ComponentPortal(
          LocationDetailsPreviewOverlayComponent,
          null,
          portalInjector,
        ),
      );
    } else {
      const data: AnswerDetailsPreviewOverlayData = {
        sections: buildAnswerDetailSections(this.value),
        summaryLabel: this.label,
        ...pointerCallbacks,
      };

      const portalInjector = Injector.create({
        parent: this.injector,
        providers: [{ provide: DIALOG_DATA, useValue: data }],
      });

      this.overlayRef.attach(
        new ComponentPortal(
          AnswerDetailsPreviewOverlayComponent,
          null,
          portalInjector,
        ),
      );
    }

    const pane = this.overlayRef.overlayElement;
    this.paneEnterHandler = () => {
      this.pointerInsideCard = true;
      this.clearCloseTimer();
    };
    this.paneLeaveHandler = () => {
      this.pointerInsideCard = false;
      this.scheduleClose();
    };
    pane.addEventListener('mouseenter', this.paneEnterHandler);
    pane.addEventListener('mouseleave', this.paneLeaveHandler);
  }

  private openLocationSheet(): void {
    const address = readAddressLikeAnswer(this.value);
    if (!address) {
      return;
    }

    this.closePreview();
    this.previewHost.claim(this, () => {
      // Sheet owns its DialogRef; host just tracks active owner.
    });

    const data: LocationDetailsPreviewOverlayData = {
      ...address,
      sheetMode: true,
    };

    this.overlayService.openBottomSheet(LocationDetailsPreviewOverlayComponent, {
      data,
      panelClass: [
        'w-full',
        'max-w-lg',
        'h-auto',
        'max-h-[85%]',
        'rounded-t-3xl',
        'overflow-clip',
        'overflow-y-auto',
      ],
    });
  }

  private isCoarsePointer(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return (
      window.matchMedia('(hover: none), (pointer: coarse)').matches ||
      window.matchMedia('(max-width: 768px)').matches
    );
  }

  private scheduleClose(): void {
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => {
      if (this.pointerInsideCard) {
        return;
      }
      this.closePreview();
    }, CLOSE_DELAY_MS);
  }

  private clearOpenTimer(): void {
    if (this.openTimer != null) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  private clearCloseTimer(): void {
    if (this.closeTimer != null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private closePreview(): void {
    this.clearOpenTimer();
    this.clearCloseTimer();
    this.closePreviewLocal();
    this.previewHost.release(this);
  }

  private closePreviewLocal(): void {
    this.detachPaneListeners();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.openingKey = null;
    this.pointerInsideCard = false;
  }

  private detachPaneListeners(): void {
    const pane = this.overlayRef?.overlayElement;
    if (pane && this.paneEnterHandler) {
      pane.removeEventListener('mouseenter', this.paneEnterHandler);
    }
    if (pane && this.paneLeaveHandler) {
      pane.removeEventListener('mouseleave', this.paneLeaveHandler);
    }
    this.paneEnterHandler = null;
    this.paneLeaveHandler = null;
  }
}
